import { DateInput, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { forwardRef, useContext, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TopicGroupTypeCode } from '../../../generated/hooks';
import { Filter, FilterOption, filterReport } from '../../../lib/reportHelper';
import { AggregatedReportDataContext } from '../../../lib/reports/aggregatedReportDataContext';
import { IReport } from '../../../lib/reports/base';
import GlobalAnswerFilter from '../GlobalAnswerFilter';
import GlobalCompareSelect from '../GlobalCompareSelect';
import GlobalGenericFilter from '../GlobalGenericFilter';
import GlobalTeamFilter from '../GlobalTeamFilter';
import { useIsMobileView } from '../ResponsiveSwitcher';
import { FiltersContainer, PredefinedFilter } from '../StyledComponents';
import { GlobalSourceFilter } from './GlobalSourceFilter';

interface IProps {
  filters: Filter[];
  options: FilterOption[];
  setFilters: (filters: Filter[]) => void;
  clearWidgets: any;
  rootTeams: string[];
  organizationName: string;
  organizationId: string;
  organizationDepth: number;
  respondentId?: string | null;
  report: IReport;
  unfilteredReport: IReport;
  className?: string;
  onConfirm: (fromOverride?: string | null, toOverride?: string | null) => void;
  addedOnFrom?: string | null;
  addedOnTo?: string | null;
  setAddedOnTo: (value: string | null) => void;
  setAddedOnFrom: (value: string | null) => void;
}

const FilterWrap = styled.div`
  min-width: 200px;
  max-width: calc((100% - 2rem) / 3);
  width: 300px;
  flex-grow: 1;

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    max-width: 100%;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${theme.spacing.f};

  @media (max-width: ${theme.breakpoint.small}) {
    border-top: 1px solid ${theme.colors.borderMain.default};
    margin-top: 0;
    margin-bottom: ${theme.spacing.c};
    margin-left: -${theme.spacing.f};
    margin-right: -${theme.spacing.f};
    padding-left: ${theme.spacing.f};
    padding-right: ${theme.spacing.m};
    padding-top: ${theme.spacing.g};
  }
`;

const StyledDateInput = styled(DateInput)`
  min-width: 200px;
  width: 100%;
  max-width: 300px;
  flex-grow: 1;
  margin-right: 1rem;
  flex: 1;

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    max-width: 100%;
  }
`;

export const GlobalFilter = forwardRef<HTMLDivElement, IProps>(
  (
    {
      options,
      filters,
      setFilters,
      rootTeams,
      organizationName,
      organizationId,
      organizationDepth,
      respondentId,
      report,
      unfilteredReport,
      onConfirm,
      className,
      addedOnFrom,
      addedOnTo,
      setAddedOnTo,
      setAddedOnFrom,
    },
    ref,
  ) => {
    const { t } = useTranslation('ReportSequence');
    const isMobile = useIsMobileView();

    const aggregatedData = useContext(AggregatedReportDataContext);

    const isHiring = report.surveyGroup?.topicGroup.typeCode === TopicGroupTypeCode.Hiring;
    const [positionsFilterOptions, recruiterFilterOptions, managersFilterOptions] = useMemo(() => {
      if (!isHiring) return [[], [], []];

      const positionsFilterOptions: { value: string; count: number }[] = [];
      const recruiterFilterOptions: { value: string; count: number }[] = [];
      const managersFilterOptions: { value: string; count: number }[] = [];

      if (report.additionalRespondentData && report.additionalRespondentData.length > 0) {
        // we need to filter the additional respondent data for each filter type - but without the filter that we are currently building
        // the reason is to display in each filter only the options that are available in combination with already applied filters
        const filteredReportForPosition = filterReport(
          unfilteredReport,
          filters.filter((f) => f.operator !== 'position'),
        );
        const channelIdsForPosition = filteredReportForPosition.responses?.map((ch) => ch.id) || [];
        const filteredRespondentDataForPosition = report.additionalRespondentData.filter((respondentData) =>
          channelIdsForPosition.includes(respondentData.channelId),
        );

        const filteredReportForRecruiter = filterReport(
          unfilteredReport,
          filters.filter((f) => f.operator !== 'recruiter'),
        );
        const channelIdsForRecruiter = filteredReportForRecruiter.responses?.map((ch) => ch.id) || [];
        const filteredRespondentDataForRecruiter = report.additionalRespondentData.filter((respondentData) =>
          channelIdsForRecruiter.includes(respondentData.channelId),
        );

        const filteredReportForManager = filterReport(
          unfilteredReport,
          filters.filter((f) => f.operator !== 'manager'),
        );
        const channelIdsForManager = filteredReportForManager.responses?.map((ch) => ch.id) || [];
        const filteredRespondentDataForManager = report.additionalRespondentData.filter((respondentData) =>
          channelIdsForManager.includes(respondentData.channelId),
        );

        filteredRespondentDataForPosition.forEach((respondent) => {
          if (respondent.position) {
            const position = positionsFilterOptions.find((option) => option.value === respondent.position);
            if (position) {
              position.count++;
            } else {
              positionsFilterOptions.push({ value: respondent.position, count: 1 });
            }
          }
        });
        filteredRespondentDataForRecruiter.forEach((respondent) => {
          if (respondent.recruiter) {
            const recruiter = recruiterFilterOptions.find((option) => option.value === respondent.recruiter);
            if (recruiter) {
              recruiter.count++;
            } else {
              recruiterFilterOptions.push({ value: respondent.recruiter, count: 1 });
            }
          }
        });
        filteredRespondentDataForManager.forEach((respondent) => {
          respondent.managers?.forEach((manager) => {
            const managerOption = managersFilterOptions.find((option) => option.value === manager);
            if (managerOption) {
              managerOption.count++;
            } else {
              managersFilterOptions.push({ value: manager, count: 1 });
            }
          });
        });
      }

      positionsFilterOptions.sort((a, b) => a.value.localeCompare(b.value));
      recruiterFilterOptions.sort((a, b) => a.value.localeCompare(b.value));
      managersFilterOptions.sort((a, b) => a.value.localeCompare(b.value));

      return [positionsFilterOptions, recruiterFilterOptions, managersFilterOptions];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(report.additionalRespondentData), JSON.stringify(filters), !!report.responses]);

    const setAddedOnAndLoad = (from: string | null, to: string | null) => {
      setAddedOnFrom(from);
      setAddedOnTo(to);
      onConfirm(from, to);
    };

    return (
      <FilterWrapper className={`bg-white rounded-lg shadow-sm px-8 py-6 ${className}`} ref={ref}>
        <FiltersScrollContainer>
          {report.surveyGroup && (
            <div>
              <PredefinedFilter
                onClick={() => {
                  const date = new Date();
                  const to = date.toISOString().split('T')[0];
                  date.setDate(date.getDate() - 30);
                  const from = date.toISOString().split('T')[0];
                  setAddedOnAndLoad(from, to);
                }}
                data-icom={'seq-filter-last30Days'}
              >
                {t('ReportSequence:lastDays', { count: 30 })}
              </PredefinedFilter>
              <PredefinedFilter
                onClick={() => {
                  const date = new Date();
                  const to = date.toISOString().split('T')[0];
                  date.setDate(date.getDate() - 90);
                  const from = date.toISOString().split('T')[0];
                  setAddedOnAndLoad(from, to);
                }}
                data-icom={'seq-filter-last90Days'}
              >
                {t('ReportSequence:lastDays', { count: 90 })}
              </PredefinedFilter>
              <PredefinedFilter
                onClick={() => {
                  const date = new Date();
                  date.setDate(1);
                  date.setDate(date.getDate() - 1);
                  const to = date.toISOString().split('T')[0];
                  date.setDate(1);
                  const from = date.toISOString().split('T')[0];
                  setAddedOnAndLoad(from, to);
                }}
                data-icom={'seq-filter-lastMonth'}
              >
                {t('ReportSequence:lastMonth')}
              </PredefinedFilter>
              <PredefinedFilter
                onClick={() => {
                  const date = new Date();
                  date.setFullYear(date.getFullYear() - 1);
                  date.setMonth(11);
                  date.setDate(31);
                  const to = date.toISOString().split('T')[0];
                  date.setMonth(0);
                  date.setDate(1);
                  const from = date.toISOString().split('T')[0];
                  setAddedOnAndLoad(from, to);
                }}
                data-icom={'seq-filter-lastYear'}
              >
                {t('ReportSequence:lastYear')}
              </PredefinedFilter>
              <PredefinedFilter
                onClick={() => {
                  setAddedOnAndLoad(null, null);
                }}
                data-icom={'seq-filter-wholePeriod'}
              >
                {t('ReportSequence:wholePeriod')}
              </PredefinedFilter>
            </div>
          )}
          <FiltersContainer>
            {report.surveyGroup && (
              <FilterWrap data-icom={'report-date-filter-from'}>
                <StyledDateInput
                  fromYear={new Date(report.sequenceFrom)}
                  toYear={new Date(report.sequenceTo)}
                  value={addedOnFrom}
                  onDateChange={setAddedOnFrom}
                  dataCy="report-date-filter"
                  baseTranslationPath="Report"
                  placeholder={t('Report:dateFilterPlaceholder')}
                  label={t(
                    report.surveyGroup?.topicGroup.typeCode === TopicGroupTypeCode.Onboarding
                      ? 'Report:labelAddedOnFromOnb'
                      : 'Report:labelAddedOnFrom',
                  )}
                />
              </FilterWrap>
            )}
            {report.surveyGroup && (
              <FilterWrap data-icom={'report-date-filter-to'}>
                <StyledDateInput
                  fromYear={new Date(report.sequenceFrom)}
                  toYear={new Date(report.sequenceTo)}
                  value={addedOnTo}
                  onDateChange={setAddedOnTo}
                  dataCy="report-date-filter"
                  baseTranslationPath="Report"
                  placeholder={t('Report:dateFilterPlaceholder')}
                  label={t(
                    report.surveyGroup?.topicGroup.typeCode === TopicGroupTypeCode.Onboarding
                      ? 'Report:labelAddedOnToOnb'
                      : 'Report:labelAddedOnTo',
                  )}
                />
              </FilterWrap>
            )}
            <FilterWrap data-icom={'report-answer-filter'} data-cy={'report-section-filter'}>
              <GlobalAnswerFilter
                options={options}
                setFilters={setFilters}
                filters={filters}
                className="same-size figure"
                organizationName={organizationName}
                organizationId={organizationId}
              />
            </FilterWrap>
            <FilterWrap data-icom={'report-teams-filter'} data-cy={'report-section-filter'}>
              <GlobalTeamFilter
                rootTeamsIds={rootTeams}
                setFilters={setFilters}
                respondentId={respondentId}
                filters={filters}
                aggregatedData={aggregatedData}
                className="same-size figure"
                organizationId={organizationId}
              />
            </FilterWrap>
            {!isMobile && (
              <FilterWrap data-icom={'report-organizaton-level-filter'} data-cy={'report-section-filter'}>
                <GlobalCompareSelect
                  setFilters={setFilters}
                  filters={filters}
                  organizationDepth={organizationDepth}
                  organizationId={organizationId}
                  aggregatedData={aggregatedData}
                  className="same-size figure no-margin"
                />
              </FilterWrap>
            )}
            {report?.source && report.source.length > 0 && (
              <FilterWrap data-icom={'report-source-filter'}>
                <GlobalSourceFilter setFilters={setFilters} filters={filters} report={report} />
              </FilterWrap>
            )}
            {isHiring && (
              <>
                <FilterWrap data-icom={'report-position-filter'}>
                  <GlobalGenericFilter
                    options={positionsFilterOptions}
                    setFilters={setFilters}
                    filters={filters}
                    disabled={positionsFilterOptions.length === 0}
                    type="position"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </FilterWrap>
                <FilterWrap data-icom={'report-recruiter-filter'}>
                  <GlobalGenericFilter
                    options={recruiterFilterOptions}
                    setFilters={setFilters}
                    filters={filters}
                    disabled={recruiterFilterOptions.length === 0}
                    type="recruiter"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </FilterWrap>
                <FilterWrap data-icom={'report-manager-filter'}>
                  <GlobalGenericFilter
                    options={managersFilterOptions}
                    setFilters={setFilters}
                    filters={filters}
                    disabled={managersFilterOptions.length === 0}
                    type="manager"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </FilterWrap>
              </>
            )}
          </FiltersContainer>
        </FiltersScrollContainer>

        <ButtonsContainer>
          <Button
            variant="outline-primary"
            data-cy={`report-filter-refresh`}
            data-icom={'report-filter-refresh'}
            onClick={() => onConfirm()}
          >
            {t('refresh')}
          </Button>
        </ButtonsContainer>
      </FilterWrapper>
    );
  },
);

export const FilterWrapper = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    padding: ${theme.spacing.f} !important;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

export const FiltersScrollContainer = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    flex-grow: 1;
    overflow-y: auto;
    margin-right: -${theme.spacing.e};
    padding-right: ${theme.spacing.e};
    padding-top: ${theme.spacing.f};
    padding-bottom: ${theme.spacing.f};
  }
`;
